import { useMutation } from '@apollo/client';
import { useCallback, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';

import { getOverlappingActivityAction } from 'common/activeElements/modals/hooks/const';
import {
	CREATABLE_EXTRA_ACTIVITY_OPTIONS,
	EXTRA_ACTIVITY_TYPES,
	getExtraActivityLocationOptions,
	getExtraActivityOptionsForStation,
} from 'common/ActivityChip/utils/extraActivities';
import { mapOverlappingActivitiesErrors } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import useOverlappingBanner from 'common/OverlappingActivitiesBanner/hooks/useOverlappingBanner';
import { WA_OVERLAPPING_ACTIVITIES_RESPONSE } from 'constants/activityTypes';
import { standardError, standardSuccess } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { durationToMinutes } from 'utils/datetime';

import {
	DEFAULT_EXTRA_ACTIVITY_DURATION,
	DEFAULT_SIGN_ON_EXTRA_ACTIVITY_DURATION,
	MANAGE_EXTRA_ACTIVITY_MODES,
	WORK_ASSIGNMENT_EXTRA_ACTIVITY_ERROR_RESPONSE,
	WORK_ASSIGNMENT_EXTRA_ACTIVITY_SUCCESS_RESPONSE,
} from './const';
import { CREATE_EXTRA_ACTIVITY_ASSIGNMENT } from './gql/createExtraActivityAssignment';
import { UPDATE_EXTRA_ACTIVITY_ASSIGNMENT } from './gql/updateExtraActivityAssignment';
import { MANAGE_EXTRA_ACTIVITY_TEXT } from './text';
import { getActivityLocationMismatchError, minutesToDuration } from './utils';

export const useManageExtraActivity = ({
	baseTimezone,
	locations = [],
	mode = MANAGE_EXTRA_ACTIVITY_MODES.add,
	selectedExtraActivity,
	availableActivityOptions: providedAvailableActivityOptions,
	initialData = {},
	lockActivityFields = false,
	onActionSuccess,
	onClose,
}) => {
	const isManageMode = mode === MANAGE_EXTRA_ACTIVITY_MODES.manage;
	const shouldLockActivityFields = isManageMode || lockActivityFields;
	const baseActivityOptions = Array.isArray(providedAvailableActivityOptions)
		? providedAvailableActivityOptions
		: CREATABLE_EXTRA_ACTIVITY_OPTIONS;
	const defaultActivityType =
		initialData.activityType ?? selectedExtraActivity ?? baseActivityOptions[0]?.type ?? '';
	const selectableLocations = useMemo(
		() => getExtraActivityLocationOptions(locations),
		[locations]
	);
	const defaultAddModeDuration =
		defaultActivityType === EXTRA_ACTIVITY_TYPES.signOn
			? DEFAULT_SIGN_ON_EXTRA_ACTIVITY_DURATION
			: DEFAULT_EXTRA_ACTIVITY_DURATION;

	const defaultValues = useMemo(
		() => ({
			stationId: shouldLockActivityFields ? (initialData.stationId ?? '') : '',
			activityType: defaultActivityType,
			duration: isManageMode
				? minutesToDuration(initialData.durationMinutes)
				: defaultAddModeDuration,
		}),
		[
			defaultActivityType,
			defaultAddModeDuration,
			initialData,
			isManageMode,
			shouldLockActivityFields,
		]
	);

	const {
		control,
		formState: { isValid },
		getValues,
		handleSubmit: handleFormSubmit,
		reset,
		setValue,
		trigger,
		watch,
	} = useForm({
		defaultValues,
		mode: 'onChange',
	});

	const {
		hasOverlappingActivities,
		cannotCancelOverlaps,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
		setOverlappingAction,
		setOverlappingActivities,
		resetOverlappingActivities,
	} = useOverlappingBanner(baseTimezone);

	useEffect(() => {
		reset(defaultValues);
		resetOverlappingActivities();
	}, [defaultValues, reset, resetOverlappingActivities]);

	const resetOverlapsOnChange = useCallback(() => {
		resetOverlappingActivities();
	}, [resetOverlappingActivities]);

	useEffect(() => {
		const subscription = watch(() => {
			resetOverlapsOnChange();
		});

		return () => subscription.unsubscribe();
	}, [watch, resetOverlapsOnChange]);

	const [createExtraActivityAssignment, { loading }] = useMutation(
		CREATE_EXTRA_ACTIVITY_ASSIGNMENT,
		{
			fetchPolicy: 'network-only',
		}
	);
	const [updateExtraActivityAssignment, { loading: isUpdating }] = useMutation(
		UPDATE_EXTRA_ACTIVITY_ASSIGNMENT,
		{
			fetchPolicy: 'network-only',
		}
	);

	const handleClose = useCallback(() => {
		reset(defaultValues);
		resetOverlappingActivities();
		onClose();
	}, [defaultValues, onClose, reset, resetOverlappingActivities]);

	const handleSuccess = useCallback(
		async (response) => {
			notificationStore.setNotification(standardSuccess);
			reset(defaultValues);
			resetOverlappingActivities();
			await onActionSuccess(response.workAssignmentSequence);
			onClose();
		},
		[defaultValues, onActionSuccess, onClose, reset, resetOverlappingActivities]
	);

	const buildInput = useCallback(() => {
		const { stationId, activityType, duration } = getValues();

		if (isManageMode) {
			return {
				workAssignmentKey: initialData.workAssignmentKey,
				durationMinutes: durationToMinutes(duration),
			};
		}

		return {
			stationId,
			sequenceGroupId: initialData.sequenceGroupId,
			rideId: initialData.rideId,
			activityType,
			durationMinutes: durationToMinutes(duration),
		};
	}, [getValues, initialData, isManageMode]);

	const handleResponse = useCallback(
		(response) => {
			if (!response) {
				notificationStore.setNotification(standardError);
				return;
			}

			if (response.__typename === WORK_ASSIGNMENT_EXTRA_ACTIVITY_SUCCESS_RESPONSE) {
				return handleSuccess(response);
			}

			if (response.__typename === WORK_ASSIGNMENT_EXTRA_ACTIVITY_ERROR_RESPONSE) {
				notificationStore.setNotification(mapOverlappingActivitiesErrors(response.errors));
				resetOverlappingActivities();
				return;
			}

			if (response.__typename === WA_OVERLAPPING_ACTIVITIES_RESPONSE) {
				setOverlappingActivities(response.overlappingActivities ?? [], {
					minDelay: response.minDelayMinutes,
					maxDelay: response.maxDelayMinutes,
				});
				return;
			}

			notificationStore.setNotification(standardError);
		},
		[handleSuccess, resetOverlappingActivities, setOverlappingActivities]
	);

	const submit = useCallback(async () => {
		try {
			const overlappingActivityAction = getOverlappingActivityAction({
				allowToDeleteOverlaps,
				allowToDelayOverlaps,
			});
			const variables = {
				input: buildInput(),
				overlappingActivityAction,
				delayMinutes: allowToDelayOverlaps ? delayMinutes : null,
			};

			const { data } = isManageMode
				? await updateExtraActivityAssignment({ variables })
				: await createExtraActivityAssignment({ variables });

			await handleResponse(
				isManageMode ? data?.updateExtraActivityAssignment : data?.createExtraActivityAssignment
			);
		} catch (error) {
			notificationStore.setNotification(standardError);
		}
	}, [
		allowToDeleteOverlaps,
		allowToDelayOverlaps,
		delayMinutes,
		buildInput,
		createExtraActivityAssignment,
		handleResponse,
		isManageMode,
		updateExtraActivityAssignment,
	]);

	const handleSubmit = handleFormSubmit(submit);

	const stationId = watch('stationId');
	const activityType = watch('activityType');
	const duration = watch('duration');
	const baseActivityTypes = baseActivityOptions.map(({ type }) => type);
	const stationActivityOptions = getExtraActivityOptionsForStation(
		stationId,
		selectableLocations
	).filter(({ type }) => baseActivityTypes.includes(type));
	const availableActivityOptions =
		stationId && !isManageMode ? stationActivityOptions : baseActivityOptions;
	const stationAllowedActivityTypes = stationActivityOptions.map(({ type }) => type);

	const isActivityTypeAllowed =
		isManageMode ||
		!activityType ||
		(baseActivityTypes.includes(activityType) &&
			(!stationId || stationAllowedActivityTypes.includes(activityType)));

	const validateActivityType = useCallback(
		(value) => {
			if (isManageMode) return true;
			if (!value) return MANAGE_EXTRA_ACTIVITY_TEXT.extraActivityRequiredError;
			if (!baseActivityTypes.includes(value)) return false;
			if (!stationId) return true;

			const allowedActivityTypes = getExtraActivityOptionsForStation(stationId, selectableLocations)
				.filter(({ type }) => baseActivityTypes.includes(type))
				.map(({ type }) => type);

			return (
				allowedActivityTypes.includes(value) ||
				getActivityLocationMismatchError(value, stationId, selectableLocations)
			);
		},
		[baseActivityTypes, isManageMode, selectableLocations, stationId]
	);

	useEffect(() => {
		if (!activityType) return;
		trigger('activityType');
	}, [activityType, stationId, trigger]);

	const isSubmitDisabled = useMemo(
		() =>
			(!isManageMode && (!stationId || !activityType)) ||
			(isManageMode &&
				(!initialData.workAssignmentKey?.id ||
					initialData.workAssignmentKey?.version === undefined ||
					initialData.workAssignmentKey?.version === null)) ||
			durationToMinutes(duration) <= 0 ||
			(!isManageMode && (!initialData.rideId || !initialData.sequenceGroupId)) ||
			!isValid ||
			!isActivityTypeAllowed ||
			(hasOverlappingActivities && !allowToDeleteOverlaps && !allowToDelayOverlaps) ||
			(hasOverlappingActivities && cannotCancelOverlaps) ||
			!!delayError,
		[
			activityType,
			allowToDeleteOverlaps,
			allowToDelayOverlaps,
			cannotCancelOverlaps,
			delayError,
			duration,
			hasOverlappingActivities,
			initialData,
			isActivityTypeAllowed,
			isManageMode,
			isValid,
			stationId,
		]
	);

	return {
		control,
		loading: loading || isUpdating,
		isSubmitDisabled,
		locations: selectableLocations,
		availableActivityOptions,
		isActivityFieldsDisabled: shouldLockActivityFields,
		hasOverlappingActivities,
		overlappingActivities,
		overlappingAction,
		isDelayOptionAvailable,
		cannotCancelOverlaps,
		handleClose,
		handleSubmit,
		setValue,
		getValues,
		validateActivityType,
		resetOverlapsOnChange,
		setOverlappingAction,
		delayMinutes,
		delayError,
		minDelayMinutes,
		setDelayMinutes,
	};
};
